import type {
  Breadcrumb,
  ErrorEvent,
  EventHint,
  NodeOptions,
} from "@sentry/nextjs";
import * as Sentry from "@sentry/nextjs";

// @sentry/nextjs doesn't re-export `StreamedSpanJSON`.
type StreamedSpanJSON = Parameters<
  NonNullable<NodeOptions["beforeSendSpan"]>
>[0];

// Production builds replace server error messages with a generic "An error
// occurred in the Server Components render..." and keep only a `digest`. The
// server-side event for the same failure carries the same digest, so tagging
// it lets `digest:<value>` in Sentry link the opaque client error to the real
// one. Next.js control-flow errors have readable digests, e.g.
// `NEXT_HTTP_ERROR_FALLBACK;404` for notFound().
export function tagErrorDigest(event: ErrorEvent, hint: EventHint) {
  const error = hint.originalException;
  if (
    error instanceof Error &&
    "digest" in error &&
    typeof error.digest === "string"
  ) {
    event.tags = { ...event.tags, digest: error.digest };
  }
  return event;
}

// `local@domain.tld`, or its URL-encoded form `local%40domain.tld`.
const EMAIL_PATTERN = /[\w.%+-]+(?:@|%40)[\w.-]+\.[a-z]{2,}/gi;

export function redactEmails(value: string) {
  return value.replace(EMAIL_PATTERN, "[email]");
}

// Where Sentry's http and fetch instrumentation store outgoing request URLs:
// `url` on breadcrumb data, `url.*` on span attributes (breadcrumbs also
// carry `url.query`).
const URL_KEYS = ["url", "url.full", "url.path", "url.query"];

function redactUrlKeys(data: Record<string, unknown> | undefined) {
  if (!data) return;
  for (const key of URL_KEYS) {
    const value = data[key];
    if (typeof value === "string") data[key] = redactEmails(value);
  }
}

// Sender.net (lib/sender.ts) puts the subscriber email in the request path,
// and Sentry records outgoing URLs in breadcrumbs and spans. Only URLs and
// breadcrumb messages are scrubbed; `setUserToSentry` keeps the email on
// purpose.
export function redactBreadcrumbEmails(breadcrumb: Breadcrumb) {
  if (breadcrumb.message) breadcrumb.message = redactEmails(breadcrumb.message);
  redactUrlKeys(breadcrumb.data);
  return breadcrumb;
}

export function redactSpanEmails(span: StreamedSpanJSON) {
  span.name = redactEmails(span.name);
  redactUrlKeys(span.attributes);
  return span;
}

export function setUserToSentry({
  userId,
  email,
}: {
  userId?: string | null;
  email?: string;
}) {
  Sentry.setUser({ id: userId || undefined, email: email });
}
